import { Close as CloseIcon } from '@mui/icons-material';
import { clearSchemaEditorError } from 'js/actions/schemaEditor';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useCdcEnvelopeWarning } from 'new/hooks/useCdcEnvelopeWarning';
import { CDC_ENVELOPE_DOCS_URL } from 'new/utils/cdcEnvelopeValidator';
import React from 'react';
import { useSelector } from 'react-redux';

interface DismissibleMessageProps {
  type: 'error' | 'warning';
  message: string;
  onDismiss: () => void;
  link?: { href: string; label: string };
}

function DismissibleMessage({ type, message, onDismiss, link }: DismissibleMessageProps) {
  return (
    <div className={`${type}-message`}>
      {message}
      {link && (
        <a href={link.href} target="_blank" rel="noreferrer">
          {link.label}
        </a>
      )}
      <CloseIcon className="close-icon" onClick={onDismiss} />
    </div>
  );
}

interface Props {
  flowType?: string;
}

export default function RecipeBuilderOutputError({ flowType }: Props) {
  const dispatch = useAppDispatch();
  const error = useSelector((state: any) => state.schemaEditor.error);
  const isEmptyOutput = useSelector((state: any) => state.schemaEditor.isEmptyOutput);

  const {
    missingKeys,
    isDismissed: envelopeWarningDismissed,
    onDismiss: dismissEnvelopeWarning,
  } = useCdcEnvelopeWarning(flowType);

  let errorMessage: string | null = null;

  if (error) {
    errorMessage = error.message;

    if (error.lineNumber && /^\d+$/.test(String(error.lineNumber))) {
      errorMessage += ` at line ${error.lineNumber}`;
    }
  } else if (isEmptyOutput) {
    errorMessage =
      'Samples and Nexset Rules produced no output. Check your logic or try different samples if this was unintended.';
  }

  const messageType = !error && isEmptyOutput ? 'warning' : 'error';

  return (
    <>
      {errorMessage && (
        <DismissibleMessage
          type={messageType}
          message={errorMessage}
          onDismiss={() => dispatch(clearSchemaEditorError())}
        />
      )}
      {missingKeys.length > 0 && !envelopeWarningDismissed && (
        <DismissibleMessage
          type="warning"
          message={`Missing required envelope keys: ${missingKeys.join(', ')}. `}
          onDismiss={dismissEnvelopeWarning}
          link={{ href: CDC_ENVELOPE_DOCS_URL, label: 'Learn more' }}
        />
      )}
    </>
  );
}
